// src/sim/ai/archetypes/scorers.ts — Hider archetype utility weight scoring (D-29..D-31).
//
// scoreHiderArchetypes derives per-tick utility weights from CharacterProfile stats.
// NO explicit archetype label, NO round-start lock, NO authored archetype field (D-29).
//
// Design:
//   deep      = hideSkill + small-bonus - fear.dark * penalty
//   move      = sprint skill - hideSkill penalty - sighting pressure * avoidance weight
//   liberate  = speedMult + courage + caught-fraction * proximity-to-plinth bonus
//   fallback-drop = 0.6 when fear > 0.5 AND profile has 'on-floor' ability (FEAR-06)
//
// All weights named in constants.ts. P7 retunes.
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-29..D-31;
//         04-RESEARCH.md Pattern 11 L1022-1070;
//         04-06-PLAN.md Task 1 action.

import type { Perception } from '../../../shared/types/perception';
import type { CharacterProfile } from '../../characters/character-profile';
import type { BlackboardEntry } from '../../../shared/types/world';
import type { Vec2 } from '../../../shared/types/intent';
import { effectiveSigma } from '../blackboard';
import {
  P4_MOVE_AVOID_WEIGHT,
  P4_LIBERATE_CAUGHT_WEIGHT,
  P4_DEEP_SMALL_SIZE_BONUS,
  P4_DEEP_DARK_FEAR_PENALTY,
  P4_MOVE_HIDE_SKILL_PENALTY,
} from './constants';

export type HiderArchetype = 'deep' | 'move' | 'liberate' | 'seek-light' | 'fallback-drop';

/**
 * Score all 4 hider archetype branches given the current Perception.
 *
 * @param perception        - Full sensory snapshot for this tick.
 * @param profile           - Per-character profile (stats drive all weights).
 * @param caughtAtBaseCount - Number of hiders currently caught at base.
 * @param totalHidersInRound - Total hiders this round (denominator for caught fraction).
 * @param plinthPosition    - Pixel-space position of the plinth zone centre.
 * @returns Record of archetype → utility score (higher = preferred).
 */
export function scoreHiderArchetypes(
  perception: Perception,
  profile: CharacterProfile,
  caughtAtBaseCount: number,
  totalHidersInRound: number,
  plinthPosition: Vec2,
  recentlyFreedSuppressLiberate: boolean = false,
): Record<HiderArchetype, number> {
  const self = perception.self;

  // --- deep ---
  const deep =
    profile.hideSkill
    + (profile.size === 'small' ? P4_DEEP_SMALL_SIZE_BONUS : 0)
    - profile.fear.dark * P4_DEEP_DARK_FEAR_PENALTY;

  // --- move --- (subtracts sighting pressure so movers stay away from spotted zones)
  //
  // POL-06 / D-11 rebalance (2026-06-14): the `- profile.hideSkill * penalty`
  // term is the "good hiders prefer to stay put" lever. P4_MOVE_HIDE_SKILL_PENALTY
  // was raised 0.3 → 0.85 (constants.ts) so a high-hideSkill character's `move`
  // drops below `deep` (it hides) while a low-hideSkill mover — whose penalty
  // term is small — keeps `move` on top (diversity preserved). Because the
  // penalty scales with hideSkill the same constant flips skilled hiders and
  // leaves movers untouched; no other term in this formula changed.
  const pressure = recentSightingPressureAtTile(self.position, perception.memory, perception.tick);
  const move =
    (profile.sprintMultiplier - 1.0) * 2
    + (profile.fear.overall < 0.5 ? 0.5 : 0)
    - profile.hideSkill * P4_MOVE_HIDE_SKILL_PENALTY
    - pressure * P4_MOVE_AVOID_WEIGHT;

  // --- liberate --- (scales with caught fraction × proximity to plinth)
  // Plan 06-13 (UAT Gap 7) deviation [Rule 2 - missing critical functionality]:
  // the prior formula computed liberate = baseSpeedMultiplier + (1 - fear.alone)
  // + P4_LIBERATE_CAUGHT_WEIGHT * fraction * (1 - distNorm). When caughtAtBaseCount
  // is 0 the third term zeroes but the first two stay, leaving liberate ~1.6 for
  // typical profiles — higher than deep (~0.75) and move (~0.95). Liberate wins,
  // its first BT condition (HasCaughtTeammatesAtBase) FAILS, the sequence
  // produces no intent, and the hider freezes (Plan 06-13 must_have: "the count
  // drops, the liberate branch loses, and the BT advances to deep/move").
  //
  // Fix: gate the entire liberate score behind caughtAtBaseCount. When zero
  // hiders are at base, liberate scores 0 and the deep/move branch wins. When
  // ≥1 are at base, the original formula applies (no behaviour change for the
  // case the original scorer was tuned for).
  const distToPlinth = Math.hypot(self.position.x - plinthPosition.x, self.position.y - plinthPosition.y);
  const mapDiagonal = Math.hypot(
    perception.map.widthInTiles * perception.map.tileWidth,
    perception.map.heightInTiles * perception.map.tileHeight,
  );
  const distNorm = Math.min(1, distToPlinth / Math.max(1, mapDiagonal));
  // Debug session freed-hiders-dont-disperse (Plan 06-13 follow-up): suppress
  // liberate for a window after liberation. The naive "selfInPlinthZone" gate
  // I tried first caused a new oscillation — freed hider walks out, gate
  // lifts, liberate wins, hider walks back, gate fires, repeat — driving
  // free-all re-emission and an audio cascade. The time-bounded suppression
  // (P4_POST_LIBERATION_LIBERATE_SUPPRESSION_TICKS, computed by the caller
  // from the entity's freedImmunityUntilTick) gives the freed hider a clean
  // dispersal window during which deep/move always wins, regardless of
  // current position relative to the plinth zone.
  const liberate = caughtAtBaseCount === 0 || recentlyFreedSuppressLiberate
    ? 0
    : profile.baseSpeedMultiplier
      + (1 - profile.fear.alone)
      + P4_LIBERATE_CAUGHT_WEIGHT * (caughtAtBaseCount / Math.max(1, totalHidersInRound)) * (1 - distNorm);

  // --- seek-light --- (D-14: afraid free hiders seek nearest permanent light to calm down)
  // Scores 0 when fear <= 0.65, when the hider is inside a hiding spot (status='hiding'),
  // or when caught. This keeps boredom (D-13) as the sole exit trigger for hiding hiders;
  // fear-based light-seeking only activates for free hiders above P4_FEAR_AFRAID=0.65.
  // Formula: (fear - 0.65) / 0.35 * 1.5 → 0 at fear=0.65, 1.5 at fear=1.0.
  // At fear=0.80: seekLight ≈ 0.643, beating deep and move for most hider profiles.
  const seekLight =
    self.fear > 0.65 && self.status !== 'hiding' && self.status !== 'caught'
      ? ((self.fear - 0.65) / 0.35) * 1.5
      : 0;

  // --- fallback-drop --- (FEAR-06: fear > 0.5 AND on-floor ability)
  // Debug session cascade-prevention (2026-04-30): also suppress for the
  // post-liberation window. A freshly-freed hider with high residual fear
  // would otherwise drop-to-floor right at the ring boundary, looking
  // visually "stuck" (the user-reported symptom 2026-04-30). Forcing them
  // through deep/move makes them path AWAY from the ring before any drop.
  const fallbackDrop =
    !recentlyFreedSuppressLiberate && self.fear > 0.5 && profile.abilities.includes('on-floor')
      ? 0.6
      : 0;

  return { deep, move, liberate, 'seek-light': seekLight, 'fallback-drop': fallbackDrop };
}

/**
 * Gaussian kernel sum over blackboard entries — how much "hunter attention"
 * sits near tile `pos`. Used by mover archetype to avoid spotted zones (D-30).
 *
 * @param pos         - Pixel-space position to sample pressure at.
 * @param memory      - Blackboard entries (hunter-team sightings).
 * @param currentTick - Current simulation tick (for effectiveSigma calculation).
 * @returns Pressure scalar — higher = more recent/confident hunter attention nearby.
 */
export function recentSightingPressureAtTile(
  pos: Vec2,
  memory: ReadonlyArray<BlackboardEntry>,
  currentTick: number,
): number {
  let total = 0;
  for (const e of memory) {
    const sigma = effectiveSigma(e, currentTick);
    const dx = pos.x - e.position.x;
    const dy = pos.y - e.position.y;
    const kernel = Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma));
    const confidenceScalar =
      e.confidence === 'high' ? 1.0
      : e.confidence === 'medium' ? 0.5
      : 0.2;
    total += kernel * confidenceScalar;
  }
  return total;
}
